/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Aug 02 2024
 **/

import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { useToast } from "@/frameworks/designStuff/components";
import { _P, _T } from "@rfice/language";
import { RestAPIError } from "@/frameworks/libraries/restservice";

import { useUnitySystemActions } from "@/frameworks/core/unity";
import { useFriend, useMyRoom, usePeople, useUsers } from "@/frameworks/core/API";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { APIErrorCode } from "@/frameworks/core/commons";
import { PeopleList } from "./peopleTypes";
import { useSidebarActions } from "../useSidebar";
import { useNetworkCommand } from "@/frameworks/core/network";
import { usePersistActions } from "@/frameworks/core/persistence";
import { useFriendListActions } from "../useFriendServiceManager";
import { useConfirm, useExtendCallAPIActions } from "@/frameworks/core/hooks";
import { useModal } from "@/frameworks/designStuff";

const LOGTAG = "#usePeopleListAction#";

export default function usePeopleListActions() {
	const { getFriendList } = useFriendListActions();
	const extendCallAPIAction = useExtendCallAPIActions();
	const dispatch = usePersistActions();
	const peopleAPI = usePeople();
	const userAPI = useUsers();
	const friendAPI = useFriend();
	const myRoomAPI = useMyRoom();
	const chatRoomActions = useChatRoomActions();
	const persistActions = usePersistActions();
	const toast = useToast();
	const { go } = useUnitySystemActions();
	const sidebarActions = useSidebarActions();
	const network = useNetworkCommand();
	const { confirm } = useConfirm();
	const modal = useModal();

	const load = useCallback(async () => {
		const data = (await peopleAPI.list.execute()).data;

		if (!data) return;

		const peopleList: Map<string, WebServices.People.List.Item> = new Map();
		data?.forEach((item) => {
			peopleList.set(item.groupId, item);
		});

		return peopleList;
	}, []);

	const {
		data: peopleList,
		isLoading,
		refetch: getPeopleList
	} = useQuery({
		queryKey: ["getPeopleList"],
		queryFn: async () => {
			const result: PeopleList = new Map();

			const data = (await peopleAPI.list.execute()).data;

			if (!data) return;

			data.forEach((item) => {
				const usersResult: Map<string, WebServices.People.List.Item["users"][number]> = new Map();
				item.users.forEach((user) => {
					usersResult.set(user.userId, user);
				});

				result.set(item.groupId, {
					...item,
					users: usersResult as Map<string, WebServices.People.List.Item["users"][number]>
				});
			});

			return result as PeopleList;
		},
		enabled: false
	});

	const handleFollowHome = async ({ userID }: { userID: string }) => {
		try {
			const { data } = await userAPI.inquiry.execute({
				userId: userID
			});
			if (!data || !Object.hasOwn(data, "myRoomId")) {
				toast.show({ type: "error", message: _T("유저 정보를 가져오는데 실패했습니다.") });
				return;
			}

			const myRoom = await myRoomAPI.inquiry.execute({
				myRoomId: data.myRoomId
			});

			if (myRoom.data && myRoom.data?.spaceScene) {
				go("userGuesture", {
					spaceType: "MY_ROOM",
					spaceId: data.myRoomId || "",
					spaceScene: myRoom.data.spaceScene
				});
			}
		} catch (error) {
			throw Error("friend api error or myRoomId not Found error");
		} finally {
			sidebarActions.hideAll();
		}
	};

	const handleChat = async (id: string, forceOpenType?: "mini" | "fullsize") => {
		try {
			const response = await chatRoomActions.dm.create({ userIDs: [id] });
			if (!response) return;
			chatRoomActions.common.open({ channelID: response.id, type: "DM" });
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.EXIST_SAME_CHAT_MEMBER: {
					const { roomId } = error.data.payload;
					// const DMRoom = chatRoomListActions.getDMRoom(roomId);
					// if (DMRoom?.hide) {
					// 	chatRoomActions.dm.hide({ channelID: roomId, hide: false });
					// }
					chatRoomActions.common.open({ channelID: roomId, type: "DM", forceOpenType });
					break;
				}
			}
		}
	};

	const handleFollowUser = async (userID: string) => {
		try {
			console.debug(LOGTAG, "handleFollowUser", userID);
			await dispatch.users.check_before_follow(userID);
			network.requestLocation(userID);
		} catch (err) {
			const error = err as RestAPIError;
			if (error.code) {
				switch (error.code) {
					case APIErrorCode.FAILED_FOLLOW_SPACE_BY_NONE_AUTHORITY:
						toast.show({ type: "error", message: _T("따라가기 할 수 없는 스페이스 입니다.") });
						return;
					case APIErrorCode.FAILED_FOLLOW_SPACE:
						toast.show({ type: "error", message: _T("따라가기 할 수 없는 스페이스 입니다.") });
						return;
					case APIErrorCode.FAILED_FOLLOW_SPACE_BY_NONE_CONFERENCE:
						toast.show({ type: "error", message: _T("따라가기 할 수 없는 스페이스 입니다.") });
						return;
					case APIErrorCode.FAILED_FOLLOW_CONFERENCE_BY_NONE_SAME_GROUP:
						toast.show({ type: "fatal", message: _T("해당 그룹 미팅/스페이스를 이용할 수 없습니다.") });
						return;
					case APIErrorCode.FAILED_FOLLOW_CONFERENCE_BECAUSE_MEETING_PRIVATE:
						toast.show({ type: "fatal", message: _T("비공개 미팅/스페이스는 따라가기 할 수 없습니다.") });
						return;
					case APIErrorCode.NOT_JOINED_SPACE:
						confirm(
							{
								title: error?.data?.name ?? "",
								descriptions: _P("스페이스 입장 시, 가입 처리 됩니다."),
								confirmText: _T("입장하기")
							},
							{
								onConfirm() {
									network.requestLocation(userID);
								},
								onCancel() {
									modal.hideAll();
								}
							}
						);
						return;
					default:
						break;
				}
			} else {
				const _error = error as Error;
				toast.show({ type: "error", message: _error.message });
			}
			throw Error("friend api error or userID not Found error");
		} finally {
			sidebarActions.hideAll();
		}
		// }
	};

	const handleFriendToggle = async (userData: Partial<WebServices.User.UserInfo>) => {
		let promise: null | Promise<WebServices.Friend.Delete.Response | WebServices.Friend.AddFriend.Response> = null;

		if (userData.friend) {
			promise = friendAPI.remove.execute({ userId: userData.userId || "" });
		} else {
			promise = friendAPI.add.execute({ userId: userData.userId || "" });

			promise
				.then(() => {
					toast.show({
						type: "success",
						message: _P("{{name}} 님과 친구가 되었습니다.", {
							name: userData.name
						}) as string
					});
				})
				.catch((error) => {
					toast.show({
						type: "system",
						message: _T("친구 요청 권한이 없습니다.")
					});
					throw error;
				});
		}

		promise?.then(async () => {
			await getFriendList();
			userData.userId && persistActions.users.refresh_user(userData.userId);
		});
	};

	return {
		load,
		isLoading,
		joinGroupIDs: peopleList ? Array.from(peopleList.values()).map((item) => item.groupId) : [],
		getPeopleList,
		handlers: {
			handleChat,
			handleFollowHome,
			handleFollowUser,
			handleFriendToggle,
			handleCall: extendCallAPIAction.ui.common.handleCall
		}
	};
}
